import React, { useRef, useContext, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import queryString from 'querystringify';

import { Search as DesignSystemSearch } from 'jl-design-system/components/search/Search';
import styles from './search.scss';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import { notifySearchTerm } from '../navigation/notify-analytics';

interface SearchProps {
  id: string;
  onFocus?: () => void;
  isTypeaheadOpen?: boolean;
  onTabOut?: () => void;
}

const loadSearchBoxFiles = (_callbackFunction: () => void) => {
  if (typeof window !== 'undefined') {
    const baseJLUrl =
      window.location.host === 'www.johnlewis.com'
        ? 'https://www.johnlewis.com'
        : 'https://www.integration.project4.com';

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `${baseJLUrl}/search-box-ui/static/index.css`;
    const cssLoaded = new Promise((resolve, reject) => {
      link.addEventListener('load', resolve);
      link.addEventListener('error', reject);
    });
    document.head.appendChild(link);

    const script = document.createElement('script');
    const jsLoaded = new Promise((resolve, reject) => {
      script.addEventListener('load', resolve);
      script.addEventListener('error', reject);
    });
    script.type = 'text/javascript';
    script.src = `${baseJLUrl}/search-box-ui/static/index.js`;
    document.head.appendChild(script);

    window.monetateQ = window.monetateQ || [];
    Promise.all([cssLoaded, jsLoaded])
      .then(() => {
        window.monetateQ?.push(['trackEvent', ['typeaheadFilesLoadSuccess']]);
        _callbackFunction();
      })
      .catch(err => {
        window.monetateQ?.push(['trackEvent', ['typeaheadFilesLoadError']]);
        console.error('Error loading basic search files ', err);
      });
  } else {
    _callbackFunction();
  }
};

type FocusableRef = {
  current: {
    focus: () => void;
  } | null;
};

export const submitFormValidation =
  (id: string, box: FocusableRef): React.FormEventHandler<HTMLFormElement> =>
  e => {
    const namedSearchTermInput = e.currentTarget.elements.namedItem(
      'search-term'
    ) as HTMLInputElement | null;
    const namedIdInput = e.currentTarget.elements.namedItem(
      id
    ) as HTMLInputElement | null;
    const idInput = document.getElementById(id) as HTMLInputElement | null;
    const input = namedSearchTermInput || namedIdInput || idInput;

    if (!input) {
      return;
    }

    if (input.value === '') {
      e.preventDefault();
      window.scrollTo(0, 0);
      box.current?.focus();
      input.focus?.();
    }
  };

export const Search = ({
  id,
  onFocus = () => {},
  isTypeaheadOpen = false,
  onTabOut = () => {},
}: SearchProps) => {
  const [loaded, setLoaded] = useState<boolean | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputKey, setSearchInputKey] = useState(0);

  const hasUserTypedRef = useRef(false);
  const { features } = useContext(
    EnvVarsFeaturesContext
  ) as EnvVarsFeaturesContextValue;

  const results = useRef<HTMLDivElement | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const box = useRef<HTMLInputElement | null>(null);

  const submitForm = submitFormValidation(id, box);

  const handleSearchSubmit = (value: string) => {
    if (!value) {
      return;
    }

    notifySearchTerm(value);

    // DesignSystemSearch handles Enter internally, so trigger native form submit.
    if (form.current) {
      if (typeof form.current.requestSubmit === 'function') {
        form.current.requestSubmit();
      } else {
        form.current.submit();
      }
    }
  };

  const handleActivation = () => {
    onFocus();

    if (!loaded) {
      loadSearchBoxFiles(() => {
        if (typeof window !== 'undefined' && window.SearchBoxUI) {
          const inputElement = document.getElementById(
            id
          ) as HTMLInputElement | null;

          window.SearchBoxUI.init(
            results?.current ?? null,
            container?.current ?? null,
            form?.current ?? null,
            inputElement,
            features.monetateExperiences,
            true,
            {}
          );
        }
      });
      setLoaded(true);
    }
  };

  useEffect(function setSearchTermFromUrl() {
    const parsedQuery: Record<string, string> = queryString.parse(
      window.location.search
    );
    const urlSearchTerm =
      parsedQuery['search-term'] || parsedQuery.originalSearchTerm;

    if (!parsedQuery.listHead && urlSearchTerm && !hasUserTypedRef.current) {
      setSearchTerm(urlSearchTerm);
      setSearchInputKey(key => key + 1);
    }
  }, []);

  useEffect(() => {
    const input = document.getElementById(id) as HTMLInputElement | null;

    if (!input) {
      return undefined;
    }

    // temporarily prevent tabbing into button - speak to JLDS
    const button = input?.parentElement?.querySelector('button');
    if (button instanceof HTMLButtonElement) {
      button.tabIndex = -1;
    }

    const onTouch = e => {
      if (e.key === 'Tab' || e.key === 'Shift') return;
      handleActivation();
    };

    button?.addEventListener('click', onTouch);
    input.addEventListener('click', onTouch);
    input.addEventListener('keyup', onTouch);

    return () => {
      button?.removeEventListener('click', onTouch);
      input.removeEventListener('click', onTouch);
      input.removeEventListener('keyup', onTouch);
    };
  }, [handleActivation, id]);

  const resetForm = () => {
    setSearchTerm('');
    window.scrollTo(0, 0);
    const inputElement = document.getElementById(id) as HTMLInputElement | null;
    inputElement?.focus();
  };

  const typeaheadResultsClasses = useMemo(
    () =>
      classNames(styles.typeaheadResults, {
        [styles.typeaheadResults__hidden]: !isTypeaheadOpen,
      }),
    [isTypeaheadOpen]
  );

  return (
    <>
      <div
        className={classNames(styles.searchFormRow, styles.container)}
        data-search-form
        ref={container}
        id="search-container"
      >
        <form
          data-testid="search-form"
          name="search-form"
          action="/search"
          className={classNames(styles.form, {
            [styles.form__expanded]: isTypeaheadOpen,
          })}
          ref={form}
          onSubmit={submitForm}
          onReset={resetForm}
        >
          <DesignSystemSearch
            key={searchInputKey}
            ref={box}
            id={id}
            ariaLabel="Search product or brand"
            labelText="Search product or brand"
            name="search-term"
            onSearch={handleSearchSubmit}
            onKeyUp={value => {
              hasUserTypedRef.current = true;
              setSearchTerm(value);
            }}
            defaultValue={searchTerm}
          />
        </form>
      </div>
      <div
        ref={results}
        className={typeaheadResultsClasses}
        onBlur={onTabOut}
      />
    </>
  );
};
